import React, { useState, useEffect, useCallback } from 'react';
import { ChevronRight, RefreshCw, Check } from 'lucide-react';
import ListPickerModal from './ListPickerModal';
import TokenImage from './TokenImage';
import { createSplitNowQuote, createSplitNowOrder } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE = process.env.REACT_APP_API_URL || 'https://iceswap.io/api';

// A couple of exchange ids from the backend don't match the icon set 1:1
const CEX_ICON_ALIASES = { gate: 'gateio', huobi: 'htx' };

const SplitNowSwap = () => {
  const { t } = useLanguage();
  const [amount, setAmount] = useState('');
  const [outputAssets, setOutputAssets] = useState([]);
  const [cexList, setCexList] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedCEX, setSelectedCEX] = useState(null);
  const [showAssetSelect, setShowAssetSelect] = useState(false);
  const [showCEXSelect, setShowCEXSelect] = useState(false);
  const [recipient, setRecipient] = useState('');
  const [refund, setRefund] = useState('');
  const [quoteId, setQuoteId] = useState(null);
  const [receiveAmount, setReceiveAmount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAssets = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/cex/assets`);
      const data = await res.json();
      setOutputAssets(data.to || []);
      setCexList(data.cex || []);
      if (data.cex?.length > 0) setSelectedCEX(data.cex[0]);
    } catch (e) {
      setError(t('splitNow.loadError'));
    }
  }, [t]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const getQuote = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 0.1) {
      setError(t('splitNow.minAmountError'));
      return;
    }
    if (!selectedAsset) {
      setError(t('splitNow.chooseCoinError'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await createSplitNowQuote({
        fromAmount: amt,
        fromAssetId: 'xmr',
        fromNetworkId: 'monero',
        toAssetId: selectedAsset.assetId,
        toNetworkId: selectedAsset.networkId,
        toExchangerId: selectedCEX?.id,
      });

      const detailRes = await fetch(`${API_BASE}/cex/quote/${data.quoteId}`);
      const details = await detailRes.json();

      setQuoteId(data.quoteId);
      setReceiveAmount(details.quoteLegs?.[0]?.quoteLegOutput?.toAmount);
    } catch (e) {
      setError(t('splitNow.quoteError'));
    } finally {
      setLoading(false);
    }
  };

  const createOrder = async () => {
    if (!recipient) {
      setError(t('splitNow.enterAddressError', { asset: selectedAsset?.symbol || '' }));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await createSplitNowOrder({
        quoteId,
        fromAmount: parseFloat(amount),
        fromAssetId: 'xmr',
        fromNetworkId: 'monero',
        toAddress: recipient,
        toPctBips: 10000,
        toAssetId: selectedAsset.assetId,
        toNetworkId: selectedAsset.networkId,
        toExchangerId: selectedCEX?.id,
        refundAddress: refund || recipient,
      });

      const orderId = data._id || data.orderId || data.id;
      if (!orderId) throw new Error('No order ID in response');

      window.location.href = `/cex/order/${orderId}`;
    } catch (e) {
      setError(e.message || t('splitNow.orderError'));
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = !!receiveAmount;

  return (
    <div className="card swap-card">
      <div className="input-group">
        <label className="input-label">{t('common.youSend')}</label>
        <div className="asset-button" style={{ cursor: 'default' }}>
          <div className="asset-info">
            <TokenImage symbol="XMR" size={40} />
            <div className="asset-details">
              <span className="asset-symbol">Monero</span>
              <span className="asset-chain">{t('splitNow.privateUntraceable')}</span>
            </div>
          </div>
          <div className="amount-input-wrapper" style={{ border: 'none', padding: 0, width: 'auto' }}>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setReceiveAmount(null);
              }}
              placeholder="0.0"
              className="amount-input"
              style={{ textAlign: 'right', width: '100px' }}
            />
          </div>
        </div>
        <p className="input-help">{t('splitNow.minMax')}</p>
      </div>

      <div className="input-group">
        <label className="input-label">{t('splitNow.viaExchange')}</label>
        <button className="asset-button" onClick={() => setShowCEXSelect(true)}>
          <div className="asset-info">
            {selectedCEX ? (
              <TokenImage symbol={CEX_ICON_ALIASES[selectedCEX.id] || selectedCEX.id} type="cex" size={40} />
            ) : (
              <TokenImage symbol="?" size={40} />
            )}
            <div className="asset-details">
              <span className="asset-symbol">{selectedCEX?.name || t('splitNow.chooseExchange')}</span>
            </div>
          </div>
          <ChevronRight className="asset-arrow" size={18} />
        </button>
      </div>

      <div className="input-group">
        <label className="input-label">{t('common.youReceive')}</label>
        <button className="asset-button" onClick={() => setShowAssetSelect(true)}>
          {selectedAsset ? (
            <div className="asset-info">
              <TokenImage symbol={selectedAsset.symbol} size={40} />
              <div className="asset-details">
                <span className="asset-symbol">{selectedAsset.symbol}</span>
                <span className="asset-chain">{selectedAsset.displayName}</span>
              </div>
            </div>
          ) : (
            <span className="asset-placeholder">{t('splitNow.chooseCoin')}</span>
          )}
          <ChevronRight className="asset-arrow" size={18} />
        </button>
      </div>

      {receiveAmount && (
        <div className="receive-box" style={{ background: 'var(--thaw-100)', borderColor: 'rgba(255,122,71,0.3)' }}>
          <span className="receive-label">{t('common.receiveEstimateLabel')}</span>
          <div className="receive-amount" style={{ color: 'var(--thaw-600)' }}>
            {receiveAmount} {selectedAsset?.symbol}
            <button onClick={getQuote} disabled={loading} className="refresh-btn" aria-label={t('splitNow.refreshQuoteAria')}>
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
            </button>
          </div>
          {selectedAsset && (
            <div className="receive-sub">{t('splitNow.minLimit', { min: selectedAsset.limits.min, symbol: selectedAsset.symbol })}</div>
          )}
        </div>
      )}

      {receiveAmount && (
        <>
          <div className="input-group" style={{ marginTop: '0.9rem' }}>
            <label className="input-label">{t('swapForm.yourAddressLabel', { asset: selectedAsset?.symbol || '' })}</label>
            <input
              className="address-input"
              placeholder={t('splitNow.pasteAddressPlaceholder', { asset: selectedAsset?.symbol || '' })}
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
            />
            <p className="input-help">{t('splitNow.addressHelp', { exchange: selectedCEX?.name || '' })}</p>
          </div>
          <div className="input-group">
            <label className="input-label">{t('common.refundAddressLabel')}</label>
            <input
              className="address-input"
              placeholder={t('splitNow.refundPlaceholder')}
              value={refund}
              onChange={(e) => setRefund(e.target.value)}
            />
          </div>
        </>
      )}

      {error && <div className="error-message">{error}</div>}

      <button className="btn btn-primary thaw" onClick={canSubmit ? createOrder : getQuote} disabled={loading}>
        {loading ? t('splitNow.working') : canSubmit ? t('common.exchangeNow') : t('common.getQuote')}
      </button>

      {showCEXSelect && (
        <ListPickerModal
          title={t('splitNow.chooseExchange')}
          items={cexList}
          itemKey={(cex) => cex.id}
          searchText={(cex) => cex.name}
          searchPlaceholder={t('splitNow.searchExchanges')}
          onClose={() => setShowCEXSelect(false)}
          renderRow={(cex) => (
            <button
              className={`list-item ${selectedCEX?.id === cex.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedCEX(cex);
                setShowCEXSelect(false);
              }}
            >
              <span className="list-item-main">
                <TokenImage symbol={CEX_ICON_ALIASES[cex.id] || cex.id} type="cex" size={36} />
                <span className="list-item-name">{cex.name}</span>
              </span>
              {selectedCEX?.id === cex.id && <Check size={18} className="list-item-check" />}
            </button>
          )}
        />
      )}

      {showAssetSelect && (
        <ListPickerModal
          title={t('splitNow.chooseCoin')}
          items={outputAssets}
          itemKey={(asset) => asset.id}
          searchText={(asset) => `${asset.symbol} ${asset.displayName}`}
          searchPlaceholder={t('splitNow.searchCoins')}
          onClose={() => setShowAssetSelect(false)}
          renderRow={(asset) => (
            <button
              className={`list-item ${selectedAsset?.id === asset.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedAsset(asset);
                setShowAssetSelect(false);
                setReceiveAmount(null);
              }}
            >
              <span className="list-item-main">
                <TokenImage symbol={asset.symbol} size={36} />
                <span>
                  <span className="list-item-name">{asset.displayName}</span>
                  <br />
                  <span className="list-item-sub">{t('splitNow.minLimit', { min: asset.limits.min, symbol: asset.symbol })}</span>
                </span>
              </span>
              {selectedAsset?.id === asset.id && <Check size={18} className="list-item-check" />}
            </button>
          )}
        />
      )}
    </div>
  );
};

export default SplitNowSwap;
